import { createMemo, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { cn } from "@/libs/cn";
import type { SectorDraft, SlotIndex } from "../../selector/types";
import { subcategoryLabel } from "../../store/taxonomy-store";
import type { JobSubcategoryKey } from "../../types";
import {
  COLOR_TRANSITION,
  FOCUS_RING,
  FullHint,
  originSuffix,
  SubCounter,
  SuggestedMark,
} from "./primitives";

const I18N = "taxonomy.sector" as const;

/**
 * A subcategory as an option row: the label on the left, the tick circle on
 * the right. The accessible name starts with the visible label; a suggested
 * row adds where it comes from («Backend, suggerito da Jev», AC3, AC27).
 *
 * Suggested is a dashed border in the ring blue (6.47:1) and a sparkle; the
 * unselected circle has a 4.8:1 border (AC29); the words never use
 * `text-primary` (AC28). Colors move over 150 ms and nothing else (M4).
 */
const SubRow = (props: {
  draft: SectorDraft;
  index: SlotIndex;
  optionKey: JobSubcategoryKey;
}) => {
  const { t } = useI18n();
  const origin = () => props.draft.originOf(props.index, props.optionKey);
  const picked = () => origin() !== null;
  const suggested = () => origin() === "suggested";
  /** At 3/3 the rows not chosen wait for one to be taken off. */
  const disabled = () => !picked() && props.draft.isFull(props.index);
  const label = () => subcategoryLabel(props.optionKey);

  return (
    <button
      type="button"
      aria-pressed={picked()}
      aria-label={
        suggested()
          ? `${label()}${originSuffix(t, props.draft.source)}`
          : undefined
      }
      disabled={disabled()}
      onClick={() => props.draft.toggleSub(props.index, props.optionKey)}
      class={cn(
        "flex h-full min-h-11 w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50",
        COLOR_TRANSITION,
        FOCUS_RING,
        suggested()
          ? "border-dashed border-ring bg-white text-foreground"
          : picked()
            ? "border-primary bg-primary/5 text-foreground"
            : "border-input bg-white text-foreground hover:border-foreground/30",
      )}
    >
      <span class="min-w-0 break-words font-medium">{label()}</span>
      <span
        class={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px]",
          COLOR_TRANSITION,
          suggested()
            ? "border-dashed border-ring bg-white text-ring"
            : picked()
              ? "border-primary bg-primary text-white"
              : "border-gray-500 bg-white",
        )}
        aria-hidden="true"
      >
        <Show
          when={suggested()}
          fallback={
            <Show when={picked()}>
              <i class="ph ph-check font-bold" />
            </Show>
          }
        >
          <SuggestedMark class="text-[10px]" />
        </Show>
      </span>
    </button>
  );
};

/**
 * The subcategories of one sector: the hint with the counter, the option rows
 * (1 column, 2 from `@md`: AC33) and the `sr-only` «Al massimo 3» line. The list takes
 * its name from the sector title and the hint, and points `aria-describedby`
 * at the full-line, always mounted, so a screen reader reads it when the list
 * is full (AC2).
 */
export const SubRows = (props: {
  draft: SectorDraft;
  index: SlotIndex;
  /** The id of the sector title this list belongs to. */
  labelledBy: string;
}) => {
  const { t } = useI18n();
  const hintId = () => `${props.labelledBy}-hint`;
  const fullId = () => `${props.labelledBy}-full`;
  /** The keys, not the option objects: `For` keeps a row while its key stays, so a pick never rebuilds the rows (focus stays). */
  const keys = createMemo(() =>
    props.draft.subOptions(props.index).map((option) => option.key),
  );
  const count = () => props.draft.slots[props.index]?.subs.length ?? 0;

  return (
    <div class="flex flex-col gap-2">
      <div class="flex items-baseline justify-between gap-2">
        <span id={hintId()} class="text-xs text-foreground/70">
          {t(`${I18N}.subcategories.title`)}
        </span>
        <SubCounter count={count()} />
      </div>
      <ul
        class="grid grid-cols-1 gap-2 @md:grid-cols-2"
        aria-labelledby={`${props.labelledBy} ${hintId()}`}
        aria-describedby={fullId()}
      >
        <For each={keys()}>
          {(key) => (
            <li>
              <SubRow draft={props.draft} index={props.index} optionKey={key} />
            </li>
          )}
        </For>
      </ul>
      <FullHint id={fullId()} show={props.draft.isFull(props.index)} />
    </div>
  );
};
